import { memo, useContext } from 'react';
import ErrorBoundary from '@/ErrorBoundary';
import { useOpenSoftPullModal } from '@/Modals/SoftPullMfeModal/useToggleSoftPullModal';
import { LOW_RESULTS_THRESHOLD } from '~/constants';
import TileType from '~/constants/TileType';
import InventoryContext from '~/contexts/InventoryContext';
import type { ConditionalTile, Vehicle } from '~/contracts';
import useCheckExperimentBucket from '~/hooks/useCheckExperimentBucket';
import { useExperimentTiles, useTiles } from './hooks';
import LowResults from './LowResultsSection';
import { ResultsSection as StyledResultsSection } from './ResultsSection.styles';
import ZeroResultsSection from './ZeroResultsSection';
import DownPaymentTile from '../DownPaymentTile';
import FinanceTile from '../FinanceTile';
import { usePagination } from '../hooks';
import KeepMovingTile from '../KeepMovingTile';
import SaveSearchTile from '../NarrowSaveTile/SaveSearchTile/SaveSearchTile';
import Pagination from '../Pagination';
import ResultTile from '../ResultTile/ResultTile';
import ExperimentResultTile from '../ResultTileV2/ResultTile';
import TradeYourCar from '../TradeYourCarTile';
import useShowZeroResults from '../useShowZeroResults';

const isVehicle = (tile: ConditionalTile | Vehicle): tile is Vehicle =>
  'vehicleId' in tile;

export default memo(function ResultsSection() {
  const {
    inventory: { isLoaded },
    similarVehicles: { vehicles: similarVehicles }
  } = useContext(InventoryContext);
  const { pagination } = usePagination();
  const openSoftPullModal = useOpenSoftPullModal();

  const isInTileExperiment = useCheckExperimentBucket(
    'srch-tile-redesign-2023',
    'experiment'
  );
  const controlTiles = useTiles(!isInTileExperiment);
  const experimentTiles = useExperimentTiles(isInTileExperiment);
  const tiles = isInTileExperiment ? experimentTiles : controlTiles;

  const hasTiles = tiles.length > 0;
  const showZeroResults = useShowZeroResults(!hasTiles);
  const showLowResults =
    hasTiles &&
    pagination.totalMatchedPages === 1 &&
    similarVehicles.length > 0 &&
    tiles.length < LOW_RESULTS_THRESHOLD &&
    isLoaded;

  const showPagination =
    hasTiles && isLoaded && (pagination.totalMatchedPages ?? 0) >= 2;

  return (
    <>
      {isLoaded ? null : (
        <StyledResultsSection
          id="results-section"
          data-qa="search-results-loading"
          isLoading={!isLoaded}
          handleFavorite={() => {}}
        >
          {Array.from(Array(24).keys()).map(x =>
            isInTileExperiment ? (
              <ExperimentResultTile key={x} />
            ) : (
              <ResultTile key={x} />
            )
          )}
        </StyledResultsSection>
      )}
      {isLoaded && showZeroResults && (
        <ErrorBoundary>
          <ZeroResultsSection />
        </ErrorBoundary>
      )}
      {isLoaded && hasTiles && (
        <StyledResultsSection
          id="results-section"
          property="itemOffered"
          typeof="Car"
        >
          {tiles.map(tile => {
            if (isVehicle(tile)) {
              return isInTileExperiment ? (
                <ExperimentResultTile tileData={tile} key={tile.vehicleId} />
              ) : (
                <ResultTile
                  key={tile.vehicleId}
                  openSoftPullModal={openSoftPullModal}
                  vehicle={tile}
                />
              );
            }

            switch (tile.tileType) {
              case TileType.DownPayment:
                return <DownPaymentTile key="finance-tile" />;
              case TileType.Finance:
                return <FinanceTile key="finance-tile" />;
              case TileType.GreatDeal:
                return <KeepMovingTile key="keep-moving-tile" />;
              case TileType.SaveSearch:
                return <SaveSearchTile key="save-search-tile" />;
              case TileType.TradeIn:
                return <TradeYourCar key="trade-in-tile" />;
              default:
                return null;
            }
          })}
        </StyledResultsSection>
      )}
      {showLowResults && <LowResults />}
      {showPagination && <Pagination />}
    </>
  );
});
